import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css';
import '../../resources/live_draw.css';

import React, { use, useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import RangeSlider from 'react-bootstrap-range-slider';
import { Navigate, useParams } from 'react-router';
import { FileModel } from '../../models/file';
import { AppContext } from '../../utils/context';
import { BlobWithName, uploadFile } from '../../utils/file_uploader';
import { assert, logError, noop } from '../../utils/misc';
import { randomString } from '../../utils/random';
import { LiveDrawManager } from './manager';

export const LiveDrawRedirectPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    assert(id);

    return <Navigate to={`/live_draw/${id}/${randomString(12)}`} />;
};

export const LiveDrawPage: React.FC = () => {
    const { apiAccessor } = use(AppContext);
    const { id, sid } = useParams<{ id: string; sid: string }>();
    assert(id);
    assert(sid);
    const [file, setFile] = useState<FileModel | undefined>(undefined);
    // eslint-disable-next-line @eslint-react/naming-convention-ref-name -- unicorn/name-replacements requires "Reference" over "Ref"
    const canvasReference = useRef<HTMLCanvasElement>(null);
    // eslint-disable-next-line @eslint-react/naming-convention-ref-name -- unicorn/name-replacements requires "Reference" over "Ref"
    const foregroundCanvasReference = useRef<HTMLCanvasElement>(null);
    // eslint-disable-next-line @eslint-react/naming-convention-ref-name -- unicorn/name-replacements requires "Reference" over "Ref"
    const backgroundCanvasReference = useRef<HTMLCanvasElement>(null);
    // eslint-disable-next-line @eslint-react/naming-convention-ref-name -- unicorn/name-replacements requires "Reference" over "Ref"
    const brushWidthSliderReference = useRef<HTMLInputElement>(null);
    // eslint-disable-next-line @eslint-react/naming-convention-ref-name -- unicorn/name-replacements requires "Reference" over "Ref"
    const managerReference = useRef<LiveDrawManager | undefined>(undefined);
    const [brushWidth, setBrushWidth] = useState(10);

    const fileName = file ? file.name : `ID_${id}`;

    useEffect(() => {
        void (async () => {
            try {
                setFile(await FileModel.getById(id, apiAccessor));
            } catch (error: unknown) {
                logError(error);
            }
        })();
    }, [id, apiAccessor]);

    const getFileName = useCallback(() => `${fileName}-edit.png`, [fileName]);

    const selectBrush = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        managerReference.current?.setBrush(event.target.value);
    }, []);

    const selectBrushWidth = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const v = Math.trunc(Number(event.target.value));
        managerReference.current?.setBrushWidth(v);
        setBrushWidth(v);
    }, []);

    const downloadImage = useCallback(() => {
        if (!canvasReference.current) {
            return;
        }

        const data = canvasReference.current.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = getFileName();
        link.href = data;
        document.body.append(link);
        link.click();
        link.remove();
    }, [getFileName]);

    const saveImage = useCallback(() => {
        if (!canvasReference.current) {
            return;
        }

        canvasReference.current.toBlob((blob) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const namedBlob = blob as BlobWithName;
            namedBlob.name = getFileName();
            void (async () => {
                try {
                    await uploadFile(namedBlob, apiAccessor);
                } catch (error: unknown) {
                    logError(error);
                }
            })();
        }, 'image/png');
    }, [getFileName, apiAccessor]);

    useEffect(() => {
        if (!canvasReference.current || !foregroundCanvasReference.current || !backgroundCanvasReference.current) {
            return noop;
        }

        const manager = new LiveDrawManager(
            canvasReference.current,
            foregroundCanvasReference.current,
            backgroundCanvasReference.current,
            setBrushWidth,
        );

        managerReference.current = manager;

        return () => {
            manager.destroy();
        };
    }, []);

    useEffect(() => {
        if (!file || !managerReference.current) {
            return;
        }

        managerReference.current.setup(file, sid);
    }, [file, sid]);

    return (
        <>
            <h1>Edit file: {fileName}</h1>
            <br />

            <div id="live-draw-wrapper">
                <canvas id="live-draw" ref={canvasReference} />
            </div>

            <canvas id="live-draw-background" ref={backgroundCanvasReference} />
            <canvas id="live-draw-foreground" ref={foregroundCanvasReference} />

            <div id="live-draw-options">
                <fieldset>
                    <legend>Brush Settings</legend>
                    <Form.Select defaultValue="brush" onChange={selectBrush}>
                        <option>rectangle</option>
                        <option>circle</option>
                        <option>brush</option>
                        <option>erase</option>
                        <option>line</option>
                        <option>restore</option>
                        <option>polygon</option>
                    </Form.Select>
                    <input id="live-draw-text-input" placeholder="draw text" type="text" />
                    <input defaultValue="Verdana" id="live-draw-font-input" placeholder="font" type="text" />
                    <br />
                    <RangeSlider
                        id="brush-width-slider"
                        max={200}
                        min={1}
                        onChange={selectBrushWidth}
                        ref={brushWidthSliderReference}
                        step={0.1}
                        value={brushWidth}
                    />
                    <br />
                    <div id="color-selector">
                        <svg id="color-selector-inner" version="1.1" xmlns="http://www.w3.org/2000/svg">
                            <line x1="0" x2="10" y1="5" y2="5" />
                            <line x1="5" x2="5" y1="0" y2="10" />
                        </svg>
                    </div>
                    <div id="saturation-selector">
                        <div id="saturation-selector-inner" />
                    </div>
                    <div id="opacity-selector">
                        <div id="opacity-selector-inner" />
                    </div>
                </fieldset>
                <fieldset>
                    <legend>Utils</legend>
                    <Button onClick={saveImage} variant="primary">
                        Save Image
                    </Button>

                    <Button onClick={downloadImage} variant="secondary">
                        Download
                    </Button>
                </fieldset>
            </div>
        </>
    );
};
